import React, {useCallback, useState} from 'react';
import {View, FlatList, Image, TouchableOpacity, Text} from 'react-native';
import type {
  RecentOrderType,
  RecentOrderItemType,
} from 'src/types/RecentOrders';
import Styles from '../../../Styles';
import AVFormattedCurrency from '../../AVFormattedCurrency';
import Pressable from '../../AVTouchableOpacity';
import {WithLocalizedProps} from 'src/types/PropTypes';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
import ChevronUp from 'src/components/img/svg/ChevronUp';
import AVText from 'src/components/elements/AVText';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getDescriber} from 'src/components/elements/orderAhead/descriptor/DescriptorType';
import {styles} from './recentOrderCanteenStyles';
import RecentOrderCanteenItem from 'src/components/elements/orderAhead/recentOrder/RecentOrderCanteenItem';
import {
  calculateTotal,
  getMenuItemUrl,
  getTimeAndDate,
} from './recentOrderUtils';

const {recentOrdersHideDetailsColors} = getDescriber();

type RecentOrderCanteenProps = WithLocalizedProps & {
  recentOrder: RecentOrderType;
  addAllItems: (order: RecentOrderType) => void;
  addItem: (item: RecentOrderItemType) => void;
  productCellPressed: (item: RecentOrderItemType) => void;
};

const RecentOrderCanteen: React.FC<RecentOrderCanteenProps> = ({
  strings,
  recentOrder,
  addAllItems,
  addItem,
  productCellPressed,
}) => {
  const hasAddAllButton =
    recentOrder.items.filter((item) => item.available === true).length > 0;
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const getShowHideLabel = useCallback(() => {
    const isMultiple = recentOrder.items.length > 1;

    if (expanded) {
      return isMultiple
        ? Localized.Labels.formatString(
            Localized.Labels.hide_details_1,
            recentOrder.items.length,
          )
        : Localized.Labels.hide_details_0;
    }

    return isMultiple
      ? Localized.Labels.formatString(
          Localized.Labels.show_details_1,
          recentOrder.items.length,
        )
      : Localized.Labels.show_details_0;
  }, [expanded, recentOrder?.items.length]);

  return (
    <View style={[styles.container]}>
      <View>
        <View style={styles.titleBar}>
          {!getMenuItemUrl(recentOrder.items[0].scancode) ? (
            <View style={styles.imageContainer}>
              <Image
                accessible
                accessibilityLabel={'Default-MenuIcon Image'}
                accessibilityRole="image"
                aria-label="Default-MenuIcon"
                role="img"
                style={styles.parentImage}
                source={require('src/components/img/DefaultMenuItem.png')}
                resizeMode="cover"
              />
            </View>
          ) : (
            <View style={styles.imageContainer}>
              <Image
                accessible={true}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
                accessibilityRole="image"
                role="img"
                source={{
                  uri: getMenuItemUrl(recentOrder.items[0].scancode),
                }}
                style={[styles.parentImage]}
                resizeMode="cover"
                testID="productImage"
              />
            </View>
          )}
          <View style={styles.titleContainer}>
            <View style={styles.rowStyle}>
              <AVText
                style={styles.titleText}
                numberOfLines={2}
                ellipsizeMode="tail"
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                accessible
                testID="qa_OrderName_txt"
                nativeID="qa_OrderName_txt"
              >
                {recentOrder.items[0].name}
              </AVText>

              {hasAddAllButton ? (
                <RoundedAppButton
                  buttonType={RoundedAppButtonType.Outline}
                  titleText={Localized.Buttons.reoder_recent_menu}
                  onPress={() => addAllItems(recentOrder)}
                  buttonViewStyle={styles.reorderButton}
                  accessibilityLabelValue="Reorder"
                  testID="qa_Reorder_Btn"
                />
              ) : (
                <View style={styles.unavailableWrapper}>
                  <Text
                    style={styles.unavailable}
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                    nativeID={`Recent_Order_${strings.Labels.unavailable}`}
                  >
                    {strings.Labels.unavailable}
                  </Text>
                </View>
              )}
            </View>
            <AVText
              style={styles.dateText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              accessible
              testID="qa_OrderDateTime_txt"
              nativeID="qa_OrderDateTime_txt"
            >
              {getTimeAndDate(recentOrder.transdate)}
            </AVText>
            <View style={styles.rowStyle}>
              <TouchableOpacity
                onPress={toggleExpand}
                accessible
                testID="qa_OrderViewDetails_btn"
              >
                <View style={styles.showHideUnderLine}>
                  <AVText
                    style={styles.showHideText}
                    numberOfLines={1}
                    maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
                  >
                    {getShowHideLabel()}
                  </AVText>
                </View>
              </TouchableOpacity>
              <AVFormattedCurrency
                style={styles.itemPrice}
                value={calculateTotal(recentOrder.items)}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
                accessibilityLabel="price"
                currency={AccountStore.getCurrency()}
                accessible
                testID="qa_ItemPrice_txt"
              />
            </View>
          </View>
        </View>
      </View>
      {expanded && (
        <View>
          <View style={styles.divider} />
          <FlatList
            data={recentOrder?.items}
            keyExtractor={(item) => item.id}
            renderItem={({item}) => {
              return (
                <RecentOrderCanteenItem
                  addItem={addItem}
                  singleCartItem={recentOrder.items.length === 1}
                  strings={strings}
                  orderItem={item}
                  productCellPressed={productCellPressed}
                  hasAddAllButton={hasAddAllButton}
                />
              );
            }}
          />
          <Pressable
            style={{alignSelf: 'center', marginBottom: Styles.Spacing.m3}}
            onPress={toggleExpand}
            nativeID={'Btn-toggleExpand-' + Localized.Labels.hide_details}
          >
            <View
              style={{
                flexDirection: 'row',
              }}
            >
              <View style={styles.showHideUnderLine}>
                <AVText
                  style={styles.hideText}
                  numberOfLines={1}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
                >
                  {Localized.Labels.hide_details}
                </AVText>
              </View>
              <View
                style={{
                  marginTop: Styles.Spacing.m1,
                  marginLeft: Styles.Spacing.m2,
                }}
              >
                <ChevronUp color={recentOrdersHideDetailsColors()} />
              </View>
            </View>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export default RecentOrderCanteen;
